import ImageBendManager from './modules/ImageBendManager';
import DrawHistory from './DrawHistory';
import { Object3D } from 'three';
import { gsap, Power1 } from 'gsap';

const EventEmitter = require('events');

class DrawManager extends EventEmitter {
  constructor(container, raycaster, camera, updatePanoFunc) {
    super();
    this.container = container;
    this.raycaster = raycaster;
    this.camera = camera;
    this.updatePano = updatePanoFunc;

    // modes DEPRECATED
    this.modes = ['BRUSH', 'IMAGE'];
    this.mode = 1;

    this.imageManager = new ImageBendManager(raycaster, camera);
    this.drawHistory = new DrawHistory();

    // local properties
    this.currentDrawer = null;
    this.currentLayer = null;
    this.layers = [];

    this.autoOrderLayers = true;
    this.enableMouse = true;
    this.isFocus = false;

    this.hasClickedObject = false;

    this.currentArtworkList = [];
  }

  setDrawMode() {
    this.mode = 1;
    this.currentDrawer = this.imageManager;
  }

  addLayer() {
    const layer = new Object3D();
    layer.userData.id = this.layers.length;
    layer.userData.mode = this.mode;
    layer.userData.order = this.layers.length;
    layer.userData.serialize = true;
    this.layers.unshift(layer);

    this.container.add(layer);
    this.setCurrentLayer(layer);
  }

  removeLayer(index) {
    this.container.remove(this.layers[index]);
    this.layers = this.layers.filter((item) => item !== this.layers[index]);
  }

  toggleVisible(index) {
    const layer = this.layers[index];
    layer.visible = !layer.visible;
  }

  setCurrentLayer(layer) {
    this.imageManager.hide();
    this.currentLayer = layer;
    this.imageManager.currentContainer = layer;
    this.imageManager.show();
    this.setDrawMode(1);
  }

  setCurrentLayerIndex(index) {
    this.imageManager.hide();
    this.currentLayer = this.layers[index];
    //    this.brush.currentContainer = this.layers[index];
    this.imageManager.currentContainer = this.layers[index];
    this.imageManager.show();

    this.setDrawMode(1);
  }

  setRenderOrder(layers) {
    let order = 0;
    layers.forEach((item, index) => {
      const layer = this.layers.find((obj) => obj.userData.id === item.id);
      layer.userData.order = order++;
    });
    this.sortLayersByOrder();
    this.debugItems(this.layers);
    this.orderLayers();
  }

  sortLayersByOrder() {
    this.layers = this.layers.sort((a, b) =>
      a.userData.order > b.userData.order ? 1 : -1,
    );
  }

  debugItems(items) {
    if (!items) items = this.state.items;
    items.forEach((item) => {
      //console.log(item.userData);
    });
  }

  orderLayers() {
    let order = 0;
    this.layers.forEach((item, index) => {
      order = this.orderDeep(item, order);
    });
  }

  orderDeep(node, order) {
    node.renderOrder = order++;
    if (node.children.length) {
      node.children.forEach((child) => {
        order = this.orderDeep(child, order);
        child.renderOrder = order++;
      });
    }

    return order;
  }

  getLayers() {
    const layers = [];
    this.layers.forEach((item, index) => {
      layers.push({
        id: `${index}`,
        content: `layer ${index}`,
        visible: item.visible,
        current: this.currentLayer.userData.id === item.userData.id,
      });
    });

    return layers;
  }

  getSerialized() {
    const result = {
      layers: [],
    };

    this.layers.forEach((item, index) => {
      const childrenData = [];

      const layer = {
        data: this.getDataForObject3D(item),
        children: this.getChildrenByUserData(childrenData, item),
      };
      if (layer.children.length) result.layers.push(layer);
    });

    return result;
  }

  getChildrenByUserData(result, item) {
    if (item.userData.serialize) {
      if (item.children.length) {
        item.children.forEach((child, index) => {
          if (child.userData.serialize) {
            const data = this.getDataForObject3D(child);
            result.push(data);

            if (child.children.length) {
              data.children = [];
              this.getChildrenByUserData(data.children, child);
            }
          }
        });
      }
    }
    return result;
  }

  getDataForObject3D(object) {
    const copy = { ...object.userData };
    if (copy.clone) {
      copy.clone = null;
    }

    if (copy.serialize) {
      copy.serialize = null;
    }

    if (copy.planeRef) {
      copy.planeRef = null;
    }
    if (copy.anchors) {
      const result = [];
      copy.anchors.forEach((item, index) => {
        result.push(item.position);
      });
      copy.anchors = result;
    }

    const data = {
      data: JSON.parse(JSON.stringify(copy, null, 2)),
      position: JSON.parse(JSON.stringify(object.position)),
      rotation: JSON.parse(JSON.stringify(object.rotation)),
      scale: JSON.parse(JSON.stringify(object.scale)),
      uuid: object.uuid,
    };
    return data;
  }

  undo() {
    const pastState = this.drawHistory.popState();
    if (pastState) {
      if (pastState.layers.length === 0) {
        this.imageManager.planes.forEach((plane) => {
          this.imageManager.currentContainer.remove(plane);
        });
        this.emit('clear-history');
      }

      pastState.layers.forEach((layer, index) => {
        if (layer.children) {
          const images = layer.children;

          // we need to check 3 things
          // (a) layer in past & current exists, we update vertices
          // (b) layer in past does not exits, but currrent does, we delete one in current
          // (c) layer in past exists, but current does not, we add in current

          // (b)
          this.imageManager.planes.forEach((plane) => {
            let hasFoundInPast = false;
            images.forEach((image) => {
              if (plane.uuid === image.uuid) {
                hasFoundInPast = true;
              }
            });
            if (!hasFoundInPast) {
              this.imageManager.currentContainer.remove(plane);
            }
          });

          images.forEach((image) => {
            const pos = image.position;
            const url = image.data.texture;
            const vertices = image.data.anchors;
            const object = this.imageManager.getPlaneForUUID(image.uuid);

            // (a)
            if (object) {
              // update vertices
              vertices.forEach((v, index) => {
                object.geometry.vertices[index].set(v.x, v.y, v.z);
                object.geometry.verticesNeedUpdate = true;
                object.geometry.boundingSphere = null;
                object.geometry.boundingBox = null;

                const anchor = object.userData.anchors[index];
                anchor.position.set(v.x, v.y, v.z);
                anchor.userData.constraintRef.position.set(v.x, v.y, v.z);
              });
            } else {
              // (c) create
              this.addImage(
                {
                  asset_id: image.data.itemId,
                  image_url: url,
                  uuid: image.uuid,
                },
                {
                  xCentered: pos.x,
                  yCentered: pos.y,
                  vertices,
                  container: image,
                },
                false,
                () => {
                  this.currentDrawer.showPlanes();
                },
              );
            }
          });
        }
      });
    } else {
      this.emit('clear-history');
    }
  }

  // ------- utils

  addImage(item, posInTexture, fromGallery, onComplete = null) {
    this.setDrawMode(1);
    //if (addLayer) this.addLayer();

    this.imageManager.addImage(item, posInTexture, fromGallery, (plane) => {
      if (onComplete) onComplete(plane);
      this.pushHistory();
      setTimeout(() => {
        this.orderLayers();
      }, 100);
    });
  }

  // ------- listeners

  onMousePressed(position) {
    this.hasClickedObject = false;

    if (!this.enableMouse) {
      this.currentDrawer.showAnchorsOnSelected = false;
      this.hasClickedObject = this.currentDrawer.onMousePressed(
        position,
        this.raycaster,
      );

      this.currentDrawer.checkFocusState();

      return this.hasClickedObject;
    }

    // this.updatePano();
    if (this.currentDrawer) {
      this.currentDrawer.showAnchorsOnSelected = true;

      this.hasClickedObject = this.currentDrawer.onMousePressed(
        position,
        this.raycaster,
      );
      if (this.hasClickedObject) {
        this.currentDrawer.placeImageOnFront();
        this.orderLayers();
      }
      return this.hasClickedObject;
    }

    return this.hasClickedObject;
  }

  onMouseMove(position) {
    const hasObject = this.currentDrawer.onMouseMove(position, this.raycaster);
    return hasObject;
  }

  onMouseDrag(position) {
    if (!this.enableMouse) return;

    if (this.currentDrawer) {
      this.currentDrawer.onMouseDrag(position, this.raycaster);
      if (this.autoOrderLayers) this.orderLayers();
    }
  }

  onCameraDrag(position) {
    if (this.currentDrawer) {
      return this.currentDrawer.onCameraDrag(position, this.raycaster);
    }
    return null;
  }

  onMouseUp(position) {
    if (!this.enableMouse) return;
    if (this.currentDrawer) {
      this.currentDrawer.onMouseUp(position);

      if (this.hasClickedObject) this.pushHistory();
    }
  }

  pushHistory() {
    // we push state to history
    const state = this.getSerialized();
    this.drawHistory.pushState(state);
    this.hasClickedObject = false;
    this.emit('push-history', { nPlanes: this.imageManager.getNumPlanes() });
  }

  load(graph, showOnLoad = false) {
    if (!graph.data) return;

    this.currentDrawer.isLoading = true;
    this.nPlanesLoaded = 0;
    // first we add layers
    this.currentArtworkList = [];

    graph.data.layers.forEach((layer, index) => {
      const { mode } = layer.data.data;
      if (layer.children && mode === 1) {
        this.setDrawMode(1);
        this.addLayer();
        const images = layer.children;
        this.nOfPlanesToLoad = images.length;

        images.forEach((image) => {
          const images = layer.children;

          if (images) {
            const pos = image.position;
            const url = image.data.texture;
            const vertices = image.data.anchors;

            this.currentArtworkList.push(image.data.itemId);

            this.addImage(
              { asset_id: image.data.itemId, image_url: url },
              {
                xCentered: pos.x,
                yCentered: pos.y,
                vertices,
                container: image,
              },
              false,
              (plane) => {
                // this is an attempt to fix the weird bug
                if (showOnLoad && plane) {
                  gsap.to(plane.material, {
                    opacity: 0.95,
                    duration: 0.5,
                    ease: Power1.easeInOut,
                  });
                }
                this.nPlanesToLoad++;
                if (this.nPlanesLoaded === this.nPlanesToLoad) {
                  this.currentDrawer.isLoading = false;
                }
              },
            );
          }
        });
      }
    });
    if (this.currentDrawer) this.currentDrawer.hide();
    this.orderLayers();
  }

  refreshCurrentList() {}

  reset() {
    this.currentArtworkList = [];
    this.clearObject(this.container);

    this.imageManager.init();
    this.addLayer();
    this.updatePano();

    this.enableMouse = true;
    this.drawHistory.clear();

    this.emit('clear-history');
    this.pushHistory();
  }

  clearObject(obj) {
    const { children } = obj;
    let child;

    if (children) {
      for (let i = children.length - 1; i >= 0; i--) {
        child = children[i];
        child.visible = false;
        obj.remove(child);
        this.clearObject(child);
      }
    }

    const { geometry } = obj;
    const { material } = obj;

    if (geometry) {
      geometry.dispose();
    }

    if (material) {
      material.dispose();
    }
  }
}

export default DrawManager;
